import { useState, useEffect, useRef } from "react";
import { Facebook, Instagram } from "lucide-react";

// TikTok icon
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Evocative phrases for rotation
const phrases = [
  "Il ritmo sta cambiando forma.",
  "Lo spazio dove la luce impara a danzare.",
  "Non sarà solo una festa. Sarà il momento in cui tutto si allinea.",
  "Settembre 2026: Preparati a perdere il respiro.",
  "L'arte dell'incontro, ridefinita."
];

// Pink Galaxy background images
const backgroundImages = [
  "https://images.unsplash.com/photo-1753211979656-587e9a5e6cd1?w=1920",
  "https://images.pexels.com/photos/12498026/pexels-photo-12498026.jpeg?auto=compress&cs=tinysrgb&w=1920",
  "https://images.unsplash.com/photo-1638722238710-11aaf1794804?w=1920",
  "https://images.unsplash.com/photo-1616001089004-04948fc0e6c1?w=1920"
];

// 3 Allestimento Fragments with codes - Mystery effect (no images)
const fragments = [
  {
    code: "CELEBRATION",
    title: "Compleanni & Battesimi",
    hint: "Atmosfera magica e sognante",
    teaser: "Dove i sogni prendono forma tra nuvole di colore...",
    icon: "✨"
  },
  {
    code: "RETAIL-VISUAL",
    title: "Vetrine Negozi",
    hint: "Impatto visivo e modernità",
    teaser: "Quando lo spazio diventa racconto luminoso...",
    icon: "💫"
  },
  {
    code: "WEDDING-LUX",
    title: "Matrimoni",
    hint: "Eleganza e romanticismo spaziale",
    teaser: "L'amore merita un universo tutto suo...",
    icon: "🌸"
  }
];

// Subtle ethereal sound
const playEtherealSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 220;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.8);
  } catch (e) {
    console.log("Audio not available");
  }
};

// Intro Screen Component
const IntroScreen = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  // Phase 0: Logo appears and grows
  // Phase 1: Logo fades, tagline appears
  // Phase 2: Everything fades out

  useEffect(() => {
    const timer1 = setTimeout(() => setPhase(1), 2500);
    const timer2 = setTimeout(() => setPhase(2), 5500);
    const timer3 = setTimeout(() => onComplete(), 7000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onComplete]);

  return (
    <div className={`intro-screen ${phase === 2 ? 'fade-out' : ''}`}>
      {/* Ethereal Pink Galaxy background for intro */}
      <div className="intro-glow-layer">
        <div className="intro-glow-orb intro-glow-1"></div>
        <div className="intro-glow-orb intro-glow-2"></div>
        <div className="intro-glow-orb intro-glow-3"></div>
        <div className="intro-sparkles"></div>
      </div>

      {/* Logo */}
      <div className={`intro-logo ${phase === 0 ? 'visible' : 'hidden'}`} data-testid="intro-title">
        <h1 className="intro-title">La Bottega delle Feste</h1>
      </div>

      {/* Tagline */}
      <div className={`intro-tagline ${phase === 1 ? 'visible' : ''}`} data-testid="intro-tagline">
        <p className="tagline-main">L'Arte che vola</p>
        <p className="tagline-secondary">il pensiero che resta</p>
      </div>

      {/* Skip button */}
      <button 
        className="skip-intro"
        onClick={onComplete}
        data-testid="skip-intro-btn"
      >
        Salta intro
      </button>
    </div>
  );
};

// Ethereal Glow Overlay Component
const EtherealOverlay = () => (
  <div className="ethereal-overlay">
    <div className="glow-orb glow-1"></div>
    <div className="glow-orb glow-2"></div>
    <div className="glow-orb glow-3"></div>
    <div className="sparkle-layer"></div>
  </div>
);

// Progress Circle Component
const AtmosphereLoader = ({ progress }) => {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  
  return (
    <div className="atmosphere-loader">
      <svg viewBox="0 0 100 100" className="progress-ring">
        <defs>
          <linearGradient id="pinkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFB6C1" />
            <stop offset="50%" stopColor="#FF69B4" />
            <stop offset="100%" stopColor="#DDA0DD" />
          </linearGradient>
        </defs>
        <circle
          className="progress-ring-bg"
          cx="50"
          cy="50"
          r="45"
          fill="none"
          strokeWidth="2"
        />
        <circle
          className="progress-ring-fill"
          cx="50"
          cy="50"
          r="45"
          fill="none"
          strokeWidth="3"
          stroke="url(#pinkGradient)"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="loader-text">
        <span className="loader-percentage">{Math.round(progress)}%</span>
      </div>
    </div>
  );
};

// Fragment Card Component - Mystery style
const FragmentCard = ({ fragment, index, onClick }) => (
  <div 
    className="fragment-card mystery-card"
    onClick={onClick}
    style={{ animationDelay: `${index * 0.2}s` }}
  >
    <div className="fragment-code">{fragment.code}</div>
    <div className="mystery-icon">{fragment.icon}</div>
    <div className="fragment-content">
      <h3 className="fragment-title">{fragment.title}</h3>
      <p className="fragment-hint">{fragment.hint}</p>
      <p className="fragment-teaser">{fragment.teaser}</p>
      <span className="coming-soon-badge">Coming Soon</span>
    </div>
  </div>
);

// Main Pink Galaxy Landing Component
const LandingMystery = () => {
  const [showIntro, setShowIntro] = useState(true);
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [progress, setProgress] = useState(0);
  const [bgIndex, setBgIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  // Skip intro after completion
  const handleIntroComplete = () => {
    setShowIntro(false);
    setTimeout(() => setIsVisible(true), 100);
  };

  // Rotate phrases
  useEffect(() => {
    if (showIntro) return;
    const interval = setInterval(() => {
      setCurrentPhrase((prev) => (prev + 1) % phrases.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [showIntro]);

  // Animate progress
  useEffect(() => {
    if (showIntro) return;
    const targetProgress = 78;
    const duration = 8000;
    const steps = 100;
    const increment = targetProgress / steps;
    const stepDuration = duration / steps;
    
    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += increment;
      if (currentProgress >= targetProgress) {
        currentProgress = targetProgress;
        clearInterval(interval);
      }
      setProgress(currentProgress);
    }, stepDuration);
    
    return () => clearInterval(interval);
  }, [showIntro]);

  // Rotate backgrounds
  useEffect(() => {
    if (showIntro) return;
    const interval = setInterval(() => {
      setBgIndex((prev) => (prev + 1) % backgroundImages.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [showIntro]);

  const handleInteraction = () => {
    playEtherealSound();
  };

  // Show intro first
  if (showIntro) {
    return <IntroScreen onComplete={handleIntroComplete} />;
  }

  return (
    <div 
      ref={containerRef}
      className={`pink-galaxy-landing ${isVisible ? 'visible' : ''}`}
      onClick={handleInteraction}
    >
      {/* Background layers */}
      <div className="bg-layers">
        {backgroundImages.map((img, idx) => (
          <div 
            key={idx}
            className={`bg-image ${idx === bgIndex ? 'active' : ''}`}
            style={{ backgroundImage: `url('${img}')` }}
          />
        ))}
      </div>
      
      {/* Ethereal glow overlay */}
      <EtherealOverlay />
      
      {/* Light gradient overlay */}
      <div className="light-overlay"></div>
      
      {/* Content */}
      <div className="galaxy-content">
        {/* Header */}
        <header className="galaxy-header">
          <div className="logo-wrapper">
            <span className="logo-text">La Bottega delle Feste</span>
          </div>
          <div className="social-icons">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-link">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-link">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="social-link">
              <TikTokIcon className="w-5 h-5" />
            </a>
          </div>
        </header>

        {/* Main hero */}
        <main className="galaxy-main">
          <div className="phrase-container">
            <p className="current-phrase" key={currentPhrase}>
              "{phrases[currentPhrase]}"
            </p>
          </div>
          
          {/* Atmosphere Loader */}
          <div className="loader-section">
            <AtmosphereLoader progress={progress} />
            <p className="sync-text">
              Sincronizzazione del battito in corso...<br/>
              <span className="date-reveal">Settembre 2026</span>
            </p>
          </div>
          
          {/* CTA */}
          <div className="cta-section">
            <a href="#allestimenti" className="galaxy-cta">
              Scopri gli Allestimenti
            </a>
            <p className="cta-hint">Tocca per sentire la magia</p>
          </div>
        </main>

        {/* Allestimenti Section */}
        <section className="allestimenti-section" id="allestimenti">
          <div className="section-header">
            <span className="section-label">Codici Allestimento</span>
            <h2 className="section-title">Tre Mondi da Scoprire</h2>
            <p className="section-subtitle">Ogni allestimento è un viaggio in un universo di eleganza</p>
          </div>
          
          <div className="fragments-grid">
            {fragments.map((fragment, index) => (
              <FragmentCard 
                key={index}
                fragment={fragment}
                index={index}
                onClick={handleInteraction}
              />
            ))}
          </div>
        </section>

        {/* Contact teaser */}
        <section className="contact-teaser" id="contatti">
          <div className="contact-content">
            <div className="contact-glow"></div>
            <h3 className="contact-title">Vuoi essere tra i primi?</h3>
            <p className="contact-text">Trissino (VI) • Settembre 2026</p>
            <div className="contact-links">
              <a href="mailto:danijela.vidakovic03@gmail.com" className="contact-link">
                danijela.vidakovic03@gmail.com
              </a>
              <a href="tel:+393661134236" className="contact-link">
                +39 366 113 4236
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="galaxy-footer">
          <p>© 2026 La Bottega delle Feste • L'attesa sta per finire</p>
        </footer>
      </div>
    </div>
  );
};

export default LandingMystery;
